import { useMutation, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import { contentVersionAnnotations } from '../../v3/contentVersionAnnotations'
import type { ReadContentVersionAnnotationsDocument } from '../../v3/contentVersionAnnotations/types'
import { globalQueryKeys } from '../queryKeys'

type ResolveParams = { id: string; resolved: boolean }

/**
 * Resolves or unresolves a pin annotation, flipping the cached resource
 * optimistically so the pin leaves (or returns to) the canvas immediately and
 * rolling back on error. Mirrors useCreateContentVersionAnnotation: the
 * optimistic write targets the raw JSON:API document the query caches; the read
 * hook's `select` re-derives the flattened list from it.
 */
export const useResolveContentVersionAnnotation = (versionUuid: string) => {
  const queryClient = useQueryClient()
  const queryKey = globalQueryKeys.contentVersionAnnotations(versionUuid)

  return useMutation({
    mutationFn: ({ id, resolved }: ResolveParams) =>
      contentVersionAnnotations.update({ id, resolved }),
    onMutate: async ({ id, resolved }: ResolveParams) => {
      await queryClient.cancelQueries(queryKey)
      const previous =
        queryClient.getQueryData<ReadContentVersionAnnotationsDocument>(
          queryKey
        )

      queryClient.setQueryData<ReadContentVersionAnnotationsDocument>(
        queryKey,
        (document) =>
          produce(document, (draft) => {
            const resource = draft?.data.find((entry) => entry.id === id)
            if (resource) resource.attributes.resolved = resolved
          })
      )

      return { previous }
    },
    onError: (_error, _params, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey)
      // Rail badges count unresolved feedback, so they move on every resolve.
      queryClient.invalidateQueries(globalQueryKeys.allContentVersions())
    },
  })
}
